import { COMPOSER_HOME_PATH } from "./learnerHomePreview";

export const getEditorPath = (item) => {
  if (!item?.id) return null;
  if (item.type === "lesson") return `/composer/courses/edit-lesson/${item.id}`;
  if (item.type === "scenario") return `/composer/courses/edit-scenario/${item.id}`;
  if (item.type === "info-page") return `/composer/courses/edit-info-page/${item.id}`;
  return null;
};

/* ── Roleplay (scenario) page paths ──
   The composer roleplay editor is split into a main page plus dedicated States and Conditions
   pages, with each state getting its own page. */
export const getScenarioEditorPath = (id) =>
  id == null ? COMPOSER_HOME_PATH : `/composer/courses/edit-scenario/${id}`;

export const getScenarioStatesPath = (id) =>
  id == null ? COMPOSER_HOME_PATH : `/composer/courses/edit-scenario/${id}/states`;

export const getScenarioStatePath = (id, stateId) =>
  id == null || stateId == null
    ? COMPOSER_HOME_PATH
    : `/composer/courses/edit-scenario/${id}/states/${encodeURIComponent(String(stateId))}`;

export const getScenarioConditionsPath = (id) =>
  id == null ? COMPOSER_HOME_PATH : `/composer/courses/edit-scenario/${id}/conditions`;

/** Scroll/expand target for a single condition card on the conditions page. */
export const getScenarioConditionContentHash = (conditionId) =>
  conditionId != null ? `ce-condition-${conditionId}` : "";

/** @returns {{ conditionId: number } | null} */
export const parseScenarioConditionContentHash = (hash) => {
  const m = /^ce-condition-(\d+)$/.exec((hash || "").replace(/^#/, ""));
  return m ? { conditionId: Number(m[1]) } : null;
};

/** @returns {{ scenarioId: string } | null} */
export const parseScenarioStatesRoute = (pathname) => {
  const m = /^\/composer\/courses\/edit-scenario\/([^/]+)\/states$/.exec(pathname || "");
  return m ? { scenarioId: m[1] } : null;
};

/** @returns {{ scenarioId: string, stateId: string } | null} */
export const parseScenarioStateRoute = (pathname) => {
  const m = /^\/composer\/courses\/edit-scenario\/([^/]+)\/states\/([^/]+)$/.exec(pathname || "");
  return m ? { scenarioId: m[1], stateId: m[2] } : null;
};

/** @returns {{ scenarioId: string } | null} */
export const parseScenarioConditionsRoute = (pathname) => {
  const m = /^\/composer\/courses\/edit-scenario\/([^/]+)\/conditions$/.exec(pathname || "");
  return m ? { scenarioId: m[1] } : null;
};

/** @deprecated Kept for URL cleanup; add-version flow uses `/version/new`. */
export const CE_ADD_VERSION_QP = "ce_add_version";
export const CE_ADD_STEP_QP = "ce_add_step";
export const CE_ADD_SUBSTEP_STEP_QP = "ce_add_substep_step";

/** Lesson editor URL with optional version (opens editor with that version selected). */
export const getLessonEditorPath = (lessonId, versionId) => {
  if (lessonId == null) return COMPOSER_HOME_PATH;
  const base = `/composer/courses/edit-lesson/${lessonId}`;
  if (versionId == null) return base;
  return `${base}?version=${encodeURIComponent(String(versionId))}`;
};

/** Full-page editor for one lesson version’s steps (and per-version annotations strip). */
export const getLessonVersionEditorPath = (lessonId, versionId) => {
  if (lessonId == null || versionId == null) return COMPOSER_HOME_PATH;
  return `/composer/courses/edit-lesson/${lessonId}/version/${versionId}`;
};

/**
 * Lesson editor URL with optional version and one-time sidebar "add" intents (see `EditLesson`).
 * `addVersion` opens the empty lesson version editor (`/version/new`).
 * @param {string|number} lessonId
 * @param {{
 *   versionId?: string|number|null,
 *   addVersion?: boolean,
 *   addStep?: boolean,
 *   addSubstepToStepId?: string|number|null,
 * }} [opts]
 */
export const getLessonEditorPathWithSidebarIntent = (lessonId, opts = {}) => {
  if (lessonId == null) return COMPOSER_HOME_PATH;
  if (opts.addVersion) {
    return getLessonVersionEditorPath(lessonId, "new");
  }
  if (opts.addStep && opts.versionId != null && opts.versionId !== "") {
    const params = new URLSearchParams();
    params.set(CE_ADD_STEP_QP, "1");
    const q = params.toString();
    const base = getLessonVersionEditorPath(lessonId, opts.versionId);
    return q ? `${base}?${q}` : base;
  }
  if (
    opts.addSubstepToStepId != null &&
    opts.addSubstepToStepId !== "" &&
    opts.versionId != null &&
    opts.versionId !== ""
  ) {
    const params = new URLSearchParams();
    params.set(CE_ADD_SUBSTEP_STEP_QP, String(opts.addSubstepToStepId));
    const q = params.toString();
    const base = getLessonVersionEditorPath(lessonId, opts.versionId);
    return q ? `${base}?${q}` : base;
  }
  const params = new URLSearchParams();
  if (opts.versionId != null && opts.versionId !== "") {
    params.set("version", String(opts.versionId));
  }
  if (opts.addStep) params.set(CE_ADD_STEP_QP, "1");
  if (opts.addSubstepToStepId != null && opts.addSubstepToStepId !== "") {
    params.set(CE_ADD_SUBSTEP_STEP_QP, String(opts.addSubstepToStepId));
  }
  const q = params.toString();
  const base = `/composer/courses/edit-lesson/${lessonId}`;
  return q ? `${base}?${q}` : base;
};

/** @returns {{ lessonId: string, versionId: string } | null} */
export const parseLessonVersionEditorRoute = (pathname) => {
  const p = pathname || "";
  const m = /^\/composer\/courses\/edit-lesson\/([^/]+)\/version\/([^/]+)$/.exec(p);
  if (m) return { lessonId: m[1], versionId: m[2] };
  return null;
};

/** Full-page editor for a single lesson step. Prefer the versioned path when a lesson version is known. */
export const getLessonStepEditorPath = (lessonId, stepId, versionId) => {
  if (lessonId == null || stepId == null) return COMPOSER_HOME_PATH;
  if (versionId != null && versionId !== "") {
    return `/composer/courses/edit-lesson/${lessonId}/version/${encodeURIComponent(
      String(versionId)
    )}/step/${encodeURIComponent(String(stepId))}`;
  }
  return `/composer/courses/edit-lesson/${lessonId}/step/${encodeURIComponent(String(stepId))}`;
};

/** @returns {{ lessonId: string, stepId: string, versionId: string | null } | null} */
export const parseLessonStepEditorRoute = (pathname) => {
  const p = pathname || "";
  const versioned = /^\/composer\/courses\/edit-lesson\/([^/]+)\/version\/([^/]+)\/step\/([^/]+)$/.exec(
    p
  );
  if (versioned) {
    return { lessonId: versioned[1], versionId: versioned[2], stepId: versioned[3] };
  }
  const legacy = /^\/composer\/courses\/edit-lesson\/([^/]+)\/step\/([^/]+)$/.exec(p);
  if (legacy) {
    return { lessonId: legacy[1], versionId: null, stepId: legacy[2] };
  }
  return null;
};

/** Full-page lesson annotations table (optional version query selects which version’s steps are listed). */
export const getLessonAnnotationsPath = (lessonId, versionId) => {
  if (lessonId == null) return COMPOSER_HOME_PATH;
  const base = `/composer/courses/edit-lesson/${lessonId}/annotations`;
  if (versionId == null) return base;
  return `${base}?version=${encodeURIComponent(String(versionId))}`;
};

/** Single annotation editor under lesson annotations (list/table scope). */
export const getLessonAnnotationDetailPath = (lessonId, annotationId, versionId) => {
  if (lessonId == null || annotationId == null) return COMPOSER_HOME_PATH;
  const base = `/composer/courses/edit-lesson/${lessonId}/annotations/${annotationId}`;
  if (versionId == null) return base;
  return `${base}?version=${encodeURIComponent(String(versionId))}`;
};

/** Full-page annotations for one step (optional version query). */
export const getLessonStepAnnotationsPath = (lessonId, stepId, versionId) => {
  if (lessonId == null || stepId == null) return COMPOSER_HOME_PATH;
  const base = `/composer/courses/edit-lesson/${lessonId}/step/${stepId}/annotations`;
  if (versionId == null) return base;
  return `${base}?version=${encodeURIComponent(String(versionId))}`;
};

/** Single annotation editor under a step’s annotations list. */
export const getLessonStepAnnotationDetailPath = (lessonId, stepId, annotationId, versionId) => {
  if (lessonId == null || stepId == null || annotationId == null) return COMPOSER_HOME_PATH;
  const base = `/composer/courses/edit-lesson/${lessonId}/step/${stepId}/annotations/${annotationId}`;
  if (versionId == null) return base;
  return `${base}?version=${encodeURIComponent(String(versionId))}`;
};

/** @returns {{ lessonId: string, annotationId: string | null } | null} */
export const parseLessonAnnotationsRoute = (pathname) => {
  const p = pathname || "";
  const detail = /^\/composer\/courses\/edit-lesson\/([^/]+)\/annotations\/([^/]+)$/.exec(p);
  if (detail) return { lessonId: detail[1], annotationId: detail[2] };
  const list = /^\/composer\/courses\/edit-lesson\/([^/]+)\/annotations$/.exec(p);
  if (list) return { lessonId: list[1], annotationId: null };
  return null;
};

/** @returns {{ lessonId: string, stepId: string, annotationId: string | null } | null} */
export const parseLessonStepAnnotationsRoute = (pathname) => {
  const p = pathname || "";
  const detail = /^\/composer\/courses\/edit-lesson\/([^/]+)\/step\/([^/]+)\/annotations\/([^/]+)$/.exec(
    p
  );
  if (detail) return { lessonId: detail[1], stepId: detail[2], annotationId: detail[3] };
  const list = /^\/composer\/courses\/edit-lesson\/([^/]+)\/step\/([^/]+)\/annotations$/.exec(p);
  if (list) return { lessonId: list[1], stepId: list[2], annotationId: null };
  return null;
};

/** Fragment targets for scrolling to a step / sub-step in the lesson editor main pane. */
export const getLessonStepContentHash = (stepId) =>
  stepId != null ? `ce-step-${stepId}` : "";

export const getLessonSubstepContentHash = (stepId, substepIndex) =>
  stepId != null && substepIndex != null
    ? `ce-substep-${stepId}-${substepIndex}`
    : "";

export const getLessonGoalContentHash = (stepId, blockIdx, goalIdx) =>
  stepId != null && blockIdx != null && goalIdx != null
    ? `ce-goal-${stepId}-${blockIdx}-${goalIdx}`
    : "";

/** Scroll target for the lesson version panel (above step cards) — sidebar "Details" under a version row. */
export const getLessonVersionPanelHash = (versionId) =>
  versionId != null ? `ce-version-${versionId}` : "";

/**
 * @returns {{ versionId: number } | null}
 */
export const parseLessonVersionPanelHash = (hash) => {
  const h = (hash || "").replace(/^#/, "");
  const m = /^ce-version-(\d+)$/.exec(h);
  if (!m) return null;
  return { versionId: Number(m[1]) };
};

/**
 * @returns {{
 *   stepId: number,
 *   substepIndex: number | null,
 *   goalItemIndex: number | null,
 * } | null}
 */
export const parseLessonContentHash = (hash) => {
  const h = (hash || "").replace(/^#/, "");
const goal = /^ce-goal-(\d+)-(\d+)-(\d+)$/.exec(h);
  if (goal) {
    return {
      stepId: Number(goal[1]),
      substepIndex: Number(goal[2]),
      goalItemIndex: Number(goal[3]),
    };
  }
  const sub = /^ce-substep-(\d+)-(\d+)$/.exec(h);
  if (sub) {
    return {
      stepId: Number(sub[1]),
      substepIndex: Number(sub[2]),
      goalItemIndex: null,
    };
  }
  const step = /^ce-step-(\d+)$/.exec(h);
  if (step) {
    return {
      stepId: Number(step[1]),
      substepIndex: null,
      goalItemIndex: null,
    };
  }
  return null;
};

/** Rebuild hash fragment (without #) from {@link parseLessonContentHash} output. */
export const serializeLessonContentHash = (parsed) => {
  if (!parsed || parsed.stepId == null) return "";
if (
    typeof parsed.goalItemIndex === "number" &&
    typeof parsed.substepIndex === "number"
  ) {
    return getLessonGoalContentHash(parsed.stepId, parsed.substepIndex, parsed.goalItemIndex);
  }
  if (typeof parsed.substepIndex === "number") {
    return getLessonSubstepContentHash(parsed.stepId, parsed.substepIndex);
  }
  return getLessonStepContentHash(parsed.stepId);
};

/**
 * Which single row in the lesson tree should show active (version panel, step, sub-step or goal).
 */
export const getDetailsTabHighlightFragment = (navFragment) => {
  const h = (navFragment || "").replace(/^#/, "");
  if (!h) return "";
  const version = parseLessonVersionPanelHash(`#${h}`);
  if (version?.versionId != null) {
    return getLessonVersionPanelHash(version.versionId);
  }
  const parsed = parseLessonContentHash(`#${h}`);
  if (!parsed || parsed.stepId == null) return "";
  if (
    typeof parsed.goalItemIndex === "number" &&
    typeof parsed.substepIndex === "number"
  ) {
    return getLessonGoalContentHash(
      parsed.stepId,
      parsed.substepIndex,
      parsed.goalItemIndex
    );
  }
if (typeof parsed.substepIndex === "number") {
    return getLessonSubstepContentHash(parsed.stepId, parsed.substepIndex);
  }
  return getLessonStepContentHash(parsed.stepId);
};

export const pathsWithQueryMatch = (pathWithQuery, pathname, search) => {
  const q = pathWithQuery.includes("?") ? `?${pathWithQuery.split("?").slice(1).join("?")}` : "";
  const p = pathWithQuery.split("?")[0];
  return p === pathname && q === (search || "");
};

/** Call before navigating to a lesson URL with a #ce-… hash so the editor may expand matching sections once. */
export const notifyLessonEditorHashLinkIntent = () => {
  window.dispatchEvent(new CustomEvent("lesson-editor-hash-navigate"));
};

/** Dispatched when the user expands a lesson step or sub-step in the editor so the course sidebar tree stays in sync. */
export const CONTENT_EDITOR_EXPAND_LESSON_BRANCH_EVENT = "content-editor-expand-lesson-branch";

/**
 * @param {{
 *   courseId: string | number,
 *   unitId?: string | number | null,
 *   lessonId: string | number,
 *   versionId: string | number,
 *   stepId: string | number,
 *   substepIndex?: number | null,
 * }} detail
 */
export const notifyContentEditorExpandLessonBranch = (detail) => {
  if (
    detail?.courseId == null ||
    detail?.lessonId == null ||
    detail?.versionId == null ||
    detail?.stepId == null
  ) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(CONTENT_EDITOR_EXPAND_LESSON_BRANCH_EVENT, { detail })
  );
};

/** Dispatched when the user collapses a lesson step or sub-step in the editor so the course sidebar tree stays in sync. */
export const CONTENT_EDITOR_COLLAPSE_LESSON_BRANCH_EVENT =
  "content-editor-collapse-lesson-branch";

/**
 * @param {object} detail
 * @param {string|number} detail.courseId
 * @param {string|number|null} [detail.unitId]
 * @param {string|number} detail.lessonId
 * @param {string|number} detail.versionId
 * @param {string|number} detail.stepId
 * @param {number|null} [detail.substepIndex] If set, only that sub-step row collapses; otherwise the whole step row collapses.
 */
export const notifyContentEditorCollapseLessonBranch = (detail) => {
  if (
    detail?.courseId == null ||
    detail?.lessonId == null ||
    detail?.versionId == null ||
    detail?.stepId == null
  ) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(CONTENT_EDITOR_COLLAPSE_LESSON_BRANCH_EVENT, { detail })
  );
};

/** Shown on disabled controls and refinement blocking overlays while AI refinement is in flight. */
export const REFINEMENT_IN_PROGRESS_TIP = "A refinement is in progress.";

/** Shown when the save control is disabled because nothing changed. */
export const SAVE_NO_CHANGES_TIP = "No changes to save.";

/** Shown when save is disabled because a save request is already running. */
export const SAVE_IN_PROGRESS_TIP = "Saving in progress…";

/**
 * Lines from leave-confirm / detail lists that describe pending (non-persisted) refinement work.
 * @param {string[]|undefined} navDetailItems
 * @returns {string[]}
 */
export function filterPendingRefinementNavLines(navDetailItems) {
  if (!Array.isArray(navDetailItems)) return [];
  return navDetailItems.filter(
    (line) =>
      typeof line === "string" &&
      (line.startsWith("Pending refinement:") || line.startsWith("Pending:"))
  );
}

/**
 * Tooltip for the save button when refinements must be resolved before saving.
 * @param {string[]} pendingLines e.g. from {@link filterPendingRefinementNavLines}
 */
export function formatPendingRefinementsSaveTooltip(pendingLines) {
  if (!pendingLines.length) return "";
  return `Accept or decline these before saving:\n\n${pendingLines
    .map((line, index) => `${index + 1}. ${line}`)
    .join("\n")}`;
}

/**
 * Formats a parenthetical list of language display names for exit-confirm dialog bullets.
 * @param {string[]} langCodes
 * @param {(lang: string) => string} formatLanguageName
 */
export function formatDetailLanguageSuffix(langCodes, formatLanguageName) {
  const labels = [...new Set((langCodes || []).map((c) => formatLanguageName(c)).filter(Boolean))];
  if (!labels.length) return "";
  return ` (${labels.join(", ")})`;
}
